import {
    createColorSetHighlight,
    createNeutralHighlight,
    createRGBATransformHighlight,
    createTransparentHighlight,
    ObjectId,
    RenderStateGroupAction,
    RenderStateHighlightGroup,
} from "@novorender/api";
import { useEffect, useMemo, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useHidden } from "contexts/hidden";
import { useHighlightCollections } from "contexts/highlightCollections";
import { useHighlighted } from "contexts/highlighted";
import { GroupStatus, ObjectGroup, useObjectGroups } from "contexts/objectGroups";
import { useSelectionBasket } from "contexts/selectionBasket";
import { selectAllDeviationGroups, selectSelectedProfile } from "features/deviations";
import { OutlineGroup, selectVisibleOutlineGroups } from "features/outlineLaser";
import { selectPropertyTreeGroups } from "features/propertyTree/slice";
import { SECTION_PLANE_HIGHLIGHT_GREEN_RGB } from "features/sectionPlane/sectionPlaneColors";
import { useFillGroupIds } from "hooks/useFillGroupIds";
import { selectIsProcoreDesign } from "slices/explorer";
import { ViewMode } from "types/misc";
import { VecRGB, VecRGBA } from "utils/color";
import { isTerrainObjectId } from "utils/objectData";

import {
    renderActions,
    selectCameraType,
    selectDefaultVisibility,
    selectMainObject,
    selectMarkerDragHoverObject,
    selectNormalColorOverrides2Objects,
    selectPicker,
    selectSectionPlaneHoverObjectId,
    selectSelectedColorOverrides2Objects,
    selectSelectionBasketColor,
    selectSelectionBasketMode,
    selectUseFilterForTransparency,
    selectViewMode,
    selectXrayColorOverrides2Objects,
    selectXrayConfig,
} from "../renderSlice";
import { CameraType, ObjectVisibility, Picker, SelectionBasketMode } from "../types";

const TRANSPARENT_HIGHLIGHT_1 = createTransparentHighlight(1);
const NEUTRAL_HIGHLIGHT = createNeutralHighlight();
const TERRAIN_HIGHLIGHT = createRGBATransformHighlight({
    red: { scale: 1, offset: 0.2 },
    green: { scale: 0.8, offset: 0 },
    blue: { scale: 0.8, offset: 0 },
});
const EMPTY_ID_ARRAY: ObjectId[] = [];

export function useHandleHighlights() {
    const {
        state: { view, forbiddenObjectIds },
    } = useExplorerGlobals();
    const mainObject = useAppSelector(selectMainObject);
    const markerDragHoverObject = useAppSelector(selectMarkerDragHoverObject);
    const highlighted = useHighlighted();
    const {
        secondaryHighlight,
        formsNew,
        formsOngoing,
        formsCompleted,
        formsFinished,
        clashObjects1,
        clashObjects2,
        transparentSelectionBase,
    } = useHighlightCollections();
    const { idArr: hidden, frozenIds: hiddenTurnedFrozenIds } = useHidden();
    const groups = useObjectGroups();
    const defaultVisibility = useAppSelector(selectDefaultVisibility);
    const useFilterForTransparency = useAppSelector(selectUseFilterForTransparency);
    const basket = useSelectionBasket();
    const dispatch = useAppDispatch();
    const basketColor = useAppSelector(selectSelectionBasketColor);
    const basketMode = useAppSelector(selectSelectionBasketMode);
    const outlineGroups = useAppSelector(selectVisibleOutlineGroups);
    const { groups: propertyTreeGroups } = useAppSelector(selectPropertyTreeGroups);
    const cameraType = useAppSelector(selectCameraType);
    const viewMode = useAppSelector(selectViewMode);
    const fillGroupIds = useFillGroupIds();
    const deviationProfileIndex = useAppSelector(selectSelectedProfile)?.index;
    const deviationLegendGroups = useAppSelector(selectAllDeviationGroups);
    const strictBasketIds = basketMode === SelectionBasketMode.Loose ? EMPTY_ID_ARRAY : basket.idArr;
    const xrayConfig = useAppSelector(selectXrayConfig);
    const normalColorOverrides2Objects = useAppSelector(selectNormalColorOverrides2Objects);
    const xRayColorOverrides2Objects = useAppSelector(selectXrayColorOverrides2Objects);
    const selectedColorOverrides2Objects = useAppSelector(selectSelectedColorOverrides2Objects);
    const picker = useAppSelector(selectPicker);
    const sectionPlaneHoverObjectId = useAppSelector(selectSectionPlaneHoverObjectId);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);

    const id = useRef(0);

    const sectionPlaneHoverHighlightGroup = useMemo((): RenderStateHighlightGroup | null => {
        if (picker !== Picker.SectionPlane || sectionPlaneHoverObjectId == null) {
            return null;
        }
        return {
            objectIds: new Uint32Array([sectionPlaneHoverObjectId]),
            action: createColorSetHighlight(SECTION_PLANE_HIGHLIGHT_GREEN_RGB),
            outlineColor: SECTION_PLANE_HIGHLIGHT_GREEN_RGB,
        };
    }, [picker, sectionPlaneHoverObjectId]);

    // Not using WeakMap for caching, because I'm afraid old groups may linger somewhere
    // like prev renderState / redux history / local component state. And cache entries may be pretty expensive.
    // Entire concept relies on all groups (object/outline/property tree) ID lists/maps to be immutable.
    // Memory wise even though we save things in cache I hope we may reduce memory pressure:
    // - Heavy things are ID maps/lists. Source ID maps/lists are stored in the app anyway.
    //   Result ID arrays are stored in view.renderState anyway.
    //   So at least we don't store anything new.
    // - Without cache we rebuild everything, so during rebuild when we have both old and new state -
    //   we consume prev_state + current_state memory.
    //   Cache allows to rebuild only changed parts so we gonna stay below previous consumption unless EVERYTHING changes at once.
    const cacheRef = useRef({
        coloredGroups: new Map<string, { idLists: Iterable<ObjectId>[]; highlightGroup: RenderStateHighlightGroup }>(),
        coloredPropertyTreeGroups: new Map<
            string,
            { idLists: { [key: number]: ObjectId }[]; highlightGroup: RenderStateHighlightGroup }
        >(),
        basketMode,
        basketIdArr: [] as ObjectId[],
        frozenGroups: new SingleFrozenHighlight(),
        hiddenGroups: {
            idLists: [] as Iterable<ObjectId>[],
            highlightGroup: undefined as undefined | RenderStateHighlightGroup,
        },
        hiddenPropertyTreeGroups: {
            idLists: [] as { [key: number]: ObjectId }[],
            highlightGroup: undefined as undefined | RenderStateHighlightGroup,
        },
        semiTransparent: new Map<ObjectGroup, RenderStateHighlightGroup>(),
        outlineGroups: new Map<OutlineGroup, RenderStateHighlightGroup>(),
        coloredDeviationGroups: {
            idLists: [] as Iterable<ObjectId>[],
            profileIndex: -1,
            highlightGroup: undefined as undefined | RenderStateHighlightGroup,
        },
    });

    const forbiddenObjectGroup = useMemo(() => {
        return forbiddenObjectIds
            ? {
                  objectIds: forbiddenObjectIds,
                  action: "filter" as const,
              }
            : undefined;
    }, [forbiddenObjectIds]);

    const hiddenHighlightGroup = useMemo(() => {
        const hiddenIds = computeHiddenIds(isProcoreDesign, hidden, hiddenTurnedFrozenIds);
        return {
            objectIds: objectIdSet([hiddenIds]).toArray(),
            action: "hide" as const,
        };
    }, [hidden, hiddenTurnedFrozenIds, isProcoreDesign]);

    // Cache simple things and whatever doesn't rely on groups with useMemo
    const basketHighlightGroup = useMemo(() => {
        return {
            objectIds: new Uint32Array(basket.idArr).sort(),
            action: basketColor.use ? createColorSetHighlight(basketColor.color) : NEUTRAL_HIGHLIGHT,
            outlineColor:
                cameraType === CameraType.Orthographic && basketColor.use
                    ? (basketColor.color.slice(0, 3) as VecRGB)
                    : undefined,
        };
    }, [basket.idArr, basketColor, cameraType]);

    const secondaryHighlightGroup = useMemo(() => {
        return {
            objectIds: new Uint32Array(
                basketMode === SelectionBasketMode.Loose
                    ? secondaryHighlight.idArr
                    : strictBasketIds.filter((id) => secondaryHighlight.ids[id]),
            ).sort(),
            action: createColorSetHighlight(secondaryHighlight.color),
        };
    }, [strictBasketIds, basketMode, secondaryHighlight]);

    const formsNewHighlightGroup = useMemo(() => {
        return {
            objectIds: new Uint32Array(
                basketMode === SelectionBasketMode.Loose
                    ? formsNew.idArr
                    : strictBasketIds.filter((id) => formsNew.ids[id]),
            ).sort(),
            action: createColorSetHighlight(formsNew.color),
        };
    }, [strictBasketIds, basketMode, formsNew]);

    const formsOngoingHighlightGroup = useMemo(() => {
        return {
            objectIds: new Uint32Array(
                basketMode === SelectionBasketMode.Loose
                    ? formsOngoing.idArr
                    : strictBasketIds.filter((id) => formsOngoing.ids[id]),
            ).sort(),
            action: createColorSetHighlight(formsOngoing.color),
        };
    }, [strictBasketIds, basketMode, formsOngoing]);

    const formsCompletedHighlightGroup = useMemo(() => {
        return {
            objectIds: new Uint32Array(
                basketMode === SelectionBasketMode.Loose
                    ? formsCompleted.idArr
                    : strictBasketIds.filter((id) => formsCompleted.ids[id]),
            ).sort(),
            action: createColorSetHighlight(formsCompleted.color),
        };
    }, [strictBasketIds, basketMode, formsCompleted]);

    const formsFinishedHighlightGroup = useMemo(() => {
        return {
            objectIds: new Uint32Array(
                basketMode === SelectionBasketMode.Loose
                    ? formsFinished.idArr
                    : strictBasketIds.filter((id) => formsFinished.ids[id]),
            ).sort(),
            action: createColorSetHighlight(formsFinished.color),
        };
    }, [strictBasketIds, basketMode, formsFinished]);

    const clashObjects1HighlightGroup = useMemo(() => {
        return {
            objectIds: new Uint32Array(
                basketMode === SelectionBasketMode.Loose
                    ? clashObjects1.idArr
                    : strictBasketIds.filter((id) => clashObjects1.ids[id]),
            ).sort(),
            action: createColorSetHighlight(clashObjects1.color),
        };
    }, [strictBasketIds, basketMode, clashObjects1]);

    const clashObjects2HighlightGroup = useMemo(() => {
        return {
            objectIds: new Uint32Array(
                basketMode === SelectionBasketMode.Loose
                    ? clashObjects2.idArr
                    : strictBasketIds.filter((id) => clashObjects2.ids[id]),
            ).sort(),
            action: createColorSetHighlight(clashObjects2.color),
        };
    }, [strictBasketIds, basketMode, clashObjects2]);

    const transparentSelectionBaseHighlightGroup = useMemo(() => {
        return {
            objectIds: new Uint32Array(transparentSelectionBase.idArr).sort(),
            action: NEUTRAL_HIGHLIGHT,
        };
    }, [transparentSelectionBase]);

    const primarySelectionIds = useMemo(
        () =>
            basketMode === SelectionBasketMode.Loose
                ? mainObject !== undefined
                    ? highlighted.idArr.concat(mainObject)
                    : highlighted.idArr
                : strictBasketIds.filter((id) => id === mainObject || highlighted.ids[id]),
        [basketMode, mainObject, highlighted, strictBasketIds],
    );

    const primaryHighlightNonTerrainGroup = useMemo(() => {
        const useNaturalColors = defaultVisibility === ObjectVisibility.Xray;
        return {
            objectIds: new Uint32Array(primarySelectionIds.filter((id) => !isTerrainObjectId(id))).sort(),
            action: useNaturalColors ? NEUTRAL_HIGHLIGHT : createColorSetHighlight(highlighted.color),
            outlineColor: [highlighted.color[0], highlighted.color[1], highlighted.color[3]] as VecRGB,
        };
    }, [primarySelectionIds, highlighted, defaultVisibility]);

    const primaryHighlightTerrainGroup = useMemo(() => {
        return {
            objectIds: new Uint32Array(primarySelectionIds.filter(isTerrainObjectId)).sort(),
            action: TERRAIN_HIGHLIGHT,
            outlineColor: [highlighted.color[0], highlighted.color[1], highlighted.color[3]] as VecRGB,
        };
    }, [primarySelectionIds, highlighted]);

    const markerDragHoverHighlightGroup = useMemo(() => {
        // Highlight for marker drag hover - works in BOTH camera modes
        if (
            markerDragHoverObject !== undefined &&
            viewMode !== ViewMode.FollowPath &&
            viewMode !== ViewMode.Deviations &&
            defaultVisibility !== ObjectVisibility.Transparent &&
            !primarySelectionIds.includes(markerDragHoverObject)
        ) {
            return {
                objectIds: new Uint32Array([markerDragHoverObject]),
                action: createColorSetHighlight([0.95, 0.388, 0.055, 1]),
                // Outline only works in ortho, but color highlight works in both
                ...(cameraType === CameraType.Orthographic && { outlineColor: [0.95, 0.388, 0.055] as VecRGB }),
            };
        }
        return undefined;
    }, [markerDragHoverObject, cameraType, viewMode, defaultVisibility, primarySelectionIds]);

    const normalColorOverrideGroups = useMemo(() => {
        if (defaultVisibility !== ObjectVisibility.Neutral) {
            return [];
        }

        return Object.entries(normalColorOverrides2Objects)
            .filter(([, objectIds]) => objectIds.length > 0)
            .map(([colorKey, objectIds]) => {
                const color = rgbaColorFromStringKey(colorKey);
                return {
                    objectIds: new Uint32Array(objectIds).sort(),
                    action: createColorSetHighlight(color),
                };
            });
    }, [normalColorOverrides2Objects, defaultVisibility]);

    const xRayColorOverrideGroups = useMemo(() => {
        if (defaultVisibility !== ObjectVisibility.Xray) {
            return [];
        }

        return Object.entries(xRayColorOverrides2Objects)
            .filter(([, objectIds]) => objectIds.length > 0)
            .map(([colorKey, objectIds]) => {
                const color = rgbaColorFromStringKey(colorKey);
                return {
                    objectIds: new Uint32Array(objectIds).sort(),
                    action: createColorSetHighlight(color),
                };
            });
    }, [xRayColorOverrides2Objects, defaultVisibility]);

    const selectedColorOverrideGroups = useMemo(() => {
        return Object.entries(selectedColorOverrides2Objects)
            .map(([colorKey, objectIds]) => {
                const selectedObjectIds = objectIds.filter((id) => primarySelectionIds.includes(id));
                return selectedObjectIds.length > 0
                    ? {
                          objectIds: new Uint32Array(selectedObjectIds),
                          action: createColorSetHighlight(rgbaColorFromStringKey(colorKey)),
                      }
                    : null;
            })
            .filter((entry) => entry !== null);
    }, [selectedColorOverrides2Objects, primarySelectionIds]);

    useEffect(() => {
        void apply();

        async function apply() {
            if (!view) {
                return;
            }

            let defaultAction: RenderStateGroupAction;
            switch (defaultVisibility) {
                case ObjectVisibility.Neutral:
                    defaultAction = NEUTRAL_HIGHLIGHT;
                    break;
                case ObjectVisibility.SemiTransparent:
                    defaultAction = createTransparentHighlight(0.2);
                    break;
                case ObjectVisibility.Xray: {
                    // Use an RGBA transform that preserves original material colors/shading
                    // while applying a gray tint and transparency. This gives a proper x-ray
                    // where inner geometry is visible through the semi-transparent outer shell,
                    // rather than a flat color replacement that makes everything look hollow.
                    const [r, g, b, a] = xrayConfig.color;
                    const tint = 0.5; // 0 = all original color, 1 = all xray tint color
                    defaultAction = createRGBATransformHighlight({
                        red: { scale: 1 - tint, offset: r * tint },
                        green: { scale: 1 - tint, offset: g * tint },
                        blue: { scale: 1 - tint, offset: b * tint },
                        opacity: a,
                    });
                    break;
                }
                default:
                    defaultAction = useFilterForTransparency ? "filter" : "hide";
            }

            view.modifyRenderState({
                highlights: {
                    defaultAction,
                },
            });

            const currentId = ++id.current;
            const loading = performance.now();
            dispatch(renderActions.addLoadingHandle(loading));
            await fillGroupIds(groups.filter((group) => group.status !== GroupStatus.None));
            dispatch(renderActions.removeLoadingHandle(loading));

            if (currentId !== id.current) {
                return;
            }

            const cache = cacheRef.current;

            const coloredDeviationGroupIds = deviationLegendGroups.filter((g) => g.isDeviationColored).map((g) => g.id);
            const { coloredDeviationGroups, coloredGroups, frozenGroups, hiddenGroups, semiTransparent } =
                groups.reduce(
                    (prev, group, idx) => {
                        switch (group.status) {
                            case GroupStatus.Selected: {
                                if (viewMode === ViewMode.Deviations && coloredDeviationGroupIds.includes(group.id)) {
                                    prev.coloredDeviationGroups.push(group.ids);
                                } else {
                                    const colorStr = group.color.toString();

                                    let colorGroups = prev.coloredGroups.get(colorStr);
                                    if (!colorGroups) {
                                        colorGroups = {
                                            idLists: [],
                                            index: idx,
                                            color: group.color,
                                            colorStr,
                                        };
                                        prev.coloredGroups.set(colorStr, colorGroups);
                                    }
                                    colorGroups.idLists.push(group.ids);
                                    colorGroups.index = idx;
                                }
                                break;
                            }
                            case GroupStatus.Hidden: {
                                if (!group.opacity) {
                                    if (group.promotedToFrozen) {
                                        prev.frozenGroups.push(group.ids);
                                    } else {
                                        prev.hiddenGroups.push(group.ids);
                                    }
                                } else {
                                    prev.semiTransparent.push(group);
                                }
                                break;
                            }
                            case GroupStatus.Frozen: {
                                prev.frozenGroups.push(group.ids);
                                break;
                            }
                            default:
                                if (
                                    viewMode === ViewMode.Deviations &&
                                    coloredDeviationGroupIds.includes(group.id) &&
                                    defaultVisibility === ObjectVisibility.Neutral
                                ) {
                                    prev.coloredDeviationGroups.push(group.ids);
                                }
                                break;
                        }

                        return prev;
                    },
                    {
                        coloredGroups: new Map<
                            string,
                            {
                                idLists: Iterable<ObjectId>[];
                                index: number;
                                color: VecRGB | VecRGBA;
                                colorStr: string;
                            }
                        >(),
                        frozenGroups: [] as Iterable<ObjectId>[],
                        hiddenGroups: [] as Iterable<ObjectId>[],
                        semiTransparent: [] as ObjectGroup[],
                        coloredDeviationGroups: [] as Iterable<ObjectId>[],
                    },
                );

            const { coloredPropertyTreeGroups, hiddenPropertyTreeGroups } = propertyTreeGroups.reduce(
                (prev, group) => {
                    switch (group.status) {
                        case GroupStatus.Selected: {
                            const colorStr = group.color.toString();

                            let colorGroups = prev.coloredPropertyTreeGroups.get(colorStr);
                            if (!colorGroups) {
                                colorGroups = {
                                    color: group.color,
                                    colorStr,
                                    ids: [],
                                };
                                prev.coloredPropertyTreeGroups.set(colorStr, colorGroups);
                            }
                            colorGroups.ids.push(group.ids);
                            break;
                        }
                        case GroupStatus.Hidden: {
                            prev.hiddenPropertyTreeGroups.push(group.ids);
                            break;
                        }
                        default:
                            break;
                    }

                    return prev;
                },
                {
                    coloredPropertyTreeGroups: new Map<
                        string,
                        {
                            ids: { [key: number]: number }[];
                            color: VecRGBA;
                            colorStr: string;
                        }
                    >(),
                    hiddenPropertyTreeGroups: [] as { [key: number]: number }[],
                },
            );

            // colored highlight groups
            keepMapKeysOfAnotherMap(cache.coloredGroups, coloredGroups);
            const coloredHighlightGroups = [...coloredGroups.values()]
                .sort((a, b) => a.index - b.index)
                .map(({ idLists, color, colorStr }) => {
                    let cacheEntry = cache.coloredGroups.get(colorStr);
                    if (
                        !cacheEntry ||
                        basketMode !== cache.basketMode ||
                        strictBasketIds !== cache.basketIdArr ||
                        !areArraysEqual(cacheEntry.idLists, idLists)
                    ) {
                        const ids = objectIdSet(idLists);
                        cacheEntry = {
                            idLists,
                            highlightGroup: {
                                objectIds:
                                    basketMode === SelectionBasketMode.Loose
                                        ? ids.toArray()
                                        : new Uint32Array(strictBasketIds.filter((id) => ids.has(id))).sort(),
                                action: createColorSetHighlight(color),
                            },
                        };
                        cache.coloredGroups.set(colorStr, cacheEntry);
                    }
                    return cacheEntry.highlightGroup;
                });

            // colored deviation groups
            if (typeof deviationProfileIndex === "number") {
                if (
                    !cache.coloredDeviationGroups.highlightGroup ||
                    cache.coloredDeviationGroups.profileIndex !== deviationProfileIndex ||
                    !areArraysEqual(cache.coloredDeviationGroups.idLists, coloredDeviationGroups)
                ) {
                    cache.coloredDeviationGroups.profileIndex = deviationProfileIndex;
                    cache.coloredDeviationGroups.idLists = coloredDeviationGroups;
                    cache.coloredDeviationGroups.highlightGroup = {
                        objectIds: objectIdSet(coloredDeviationGroups).toArray(),
                        action: TRANSPARENT_HIGHLIGHT_1,
                        pointVisualization: {
                            kind: "deviation" as const,
                            index: deviationProfileIndex,
                        },
                    };
                }
            } else {
                cache.coloredDeviationGroups.profileIndex = -1;
                cache.coloredDeviationGroups.idLists = [];
                cache.coloredDeviationGroups.highlightGroup = undefined;
            }

            // semi transparent
            const semiTransparentHighlightGroups: RenderStateHighlightGroup[] = [];
            if (defaultVisibility === ObjectVisibility.Neutral) {
                keepMapKeys(cache.semiTransparent, semiTransparent);
                for (const group of semiTransparent) {
                    let highlight = cache.semiTransparent.get(group);
                    if (!highlight) {
                        highlight = {
                            objectIds: group.ids.asSortedIterable(),
                            action: createTransparentHighlight(group.opacity),
                        };
                        cache.semiTransparent.set(group, highlight);
                    }
                    semiTransparentHighlightGroups.push(highlight);
                }
            } else {
                cache.semiTransparent.clear();
            }

            // outline groups
            const outlineHighlightGroups: RenderStateHighlightGroup[] = [];
            if (
                cameraType === CameraType.Orthographic &&
                viewMode !== ViewMode.FollowPath &&
                viewMode !== ViewMode.Deviations &&
                defaultVisibility !== ObjectVisibility.Transparent
            ) {
                keepMapKeys(cache.outlineGroups, outlineGroups);
                for (const group of outlineGroups) {
                    let highlight = cache.outlineGroups.get(group);
                    if (!highlight) {
                        highlight = {
                            objectIds: new Uint32Array(group.ids).sort(),
                            action: NEUTRAL_HIGHLIGHT, // ???
                            outlineColor: group.color,
                        };
                        cache.outlineGroups.set(group, highlight);
                    }
                    outlineHighlightGroups.push(highlight);
                }
            } else {
                cache.outlineGroups.clear();
            }

            // colored property tree groups
            keepMapKeysOfAnotherMap(cache.coloredPropertyTreeGroups, coloredPropertyTreeGroups);
            const coloredPropertyTreeHighlightGroups = [...coloredPropertyTreeGroups.values()].map(
                ({ ids, color, colorStr }) => {
                    let cacheEntry = cache.coloredPropertyTreeGroups.get(colorStr);
                    if (
                        !cacheEntry ||
                        strictBasketIds !== cache.basketIdArr ||
                        basketMode !== cache.basketMode ||
                        !areArraysEqual(cacheEntry.idLists, ids)
                    ) {
                        const idSet = objectIdSet(ids.map((obj) => Object.values(obj)));
                        cacheEntry = {
                            idLists: ids,
                            highlightGroup: {
                                objectIds:
                                    basketMode === SelectionBasketMode.Loose
                                        ? idSet.toArray()
                                        : strictBasketIds.filter((id) => idSet.has(id)),
                                action: createColorSetHighlight(color),
                            },
                        };
                        cache.coloredPropertyTreeGroups.set(colorStr, cacheEntry);
                    }
                    return cacheEntry.highlightGroup;
                },
            );

            // hidden groups
            if (!cache.hiddenGroups.highlightGroup || !areArraysEqual(cache.hiddenGroups.idLists, hiddenGroups)) {
                cache.hiddenGroups.idLists = hiddenGroups;
                cache.hiddenGroups.highlightGroup = {
                    objectIds: objectIdSet(hiddenGroups).toArray(),
                    action: "hide",
                };
            }

            // hidden property tree groups
            if (
                !cache.hiddenPropertyTreeGroups.highlightGroup ||
                !areArraysEqual(cache.hiddenPropertyTreeGroups.idLists, hiddenPropertyTreeGroups)
            ) {
                cache.hiddenPropertyTreeGroups.idLists = hiddenPropertyTreeGroups;
                cache.hiddenPropertyTreeGroups.highlightGroup = {
                    objectIds: objectIdSet(hiddenPropertyTreeGroups.map((ids) => Object.values(ids))).toArray(),
                    action: "hide",
                };
            }

            cache.basketMode = basketMode;
            cache.basketIdArr = strictBasketIds;

            const postFrozenHighlights = buildPostFrozenHighlights(isProcoreDesign, {
                cacheHiddenGroups: cache.hiddenGroups.highlightGroup,
                cacheHiddenPropertyTreeGroups: cache.hiddenPropertyTreeGroups.highlightGroup,
                hiddenHighlight: hiddenHighlightGroup,
                basket: basketHighlightGroup,
                normalColorOverrides: normalColorOverrideGroups,
                xRayColorOverrides: xRayColorOverrideGroups,
                colored: coloredHighlightGroups,
                coloredPropertyTree: coloredPropertyTreeHighlightGroups,
                coloredDeviation: cache.coloredDeviationGroups.highlightGroup,
                secondary: secondaryHighlightGroup,
                formsNew: formsNewHighlightGroup,
                formsOngoing: formsOngoingHighlightGroup,
                formsCompleted: formsCompletedHighlightGroup,
                formsFinished: formsFinishedHighlightGroup,
                clash1: clashObjects1HighlightGroup,
                clash2: clashObjects2HighlightGroup,
                markerDragHover: markerDragHoverHighlightGroup,
                primaryNonTerrain: primaryHighlightNonTerrainGroup,
                primaryTerrain: primaryHighlightTerrainGroup,
                sectionPlaneHover: sectionPlaneHoverHighlightGroup,
                selectedColorOverrides: selectedColorOverrideGroups,
                forbidden: forbiddenObjectGroup,
            });

            // Batch frozen objects together with frozen groups to have a single frozen highlight group
            if (hiddenTurnedFrozenIds.size > 0) {
                frozenGroups.push(hiddenTurnedFrozenIds);
            }

            // frozen
            const frozenHighlightGroup = cache.frozenGroups.update(frozenGroups, postFrozenHighlights);

            // Update render state
            const allGroups = [
                ...semiTransparentHighlightGroups,
                ...outlineHighlightGroups,
                transparentSelectionBaseHighlightGroup,
                frozenHighlightGroup,
                ...postFrozenHighlights,
            ].filter(Boolean);

            view.modifyRenderState({
                highlights: {
                    defaultAction,
                    groups: allGroups,
                },
            });
        }
    }, [
        view,
        dispatch,
        transparentSelectionBaseHighlightGroup,
        forbiddenObjectGroup,
        secondaryHighlightGroup,
        formsNewHighlightGroup,
        formsOngoingHighlightGroup,
        formsCompletedHighlightGroup,
        formsFinishedHighlightGroup,
        clashObjects1HighlightGroup,
        clashObjects2HighlightGroup,
        basketHighlightGroup,
        markerDragHoverHighlightGroup,
        primaryHighlightNonTerrainGroup,
        primaryHighlightTerrainGroup,
        sectionPlaneHoverHighlightGroup,
        hiddenTurnedFrozenIds,
        hiddenHighlightGroup,
        groups,
        propertyTreeGroups,
        defaultVisibility,
        useFilterForTransparency,
        strictBasketIds,
        basketMode,
        outlineGroups,
        cameraType,
        viewMode,
        fillGroupIds,
        deviationProfileIndex,
        deviationLegendGroups,
        xrayConfig,
        normalColorOverrideGroups,
        selectedColorOverrideGroups,
        xRayColorOverrideGroups,
        isProcoreDesign,
    ]);
}

/**
 * The idea is to use array (index is object ID) instead of set if ID range is
 * so array is not too big and pretty close or smaller than total ID count of the underlying sets.
 * @param idSets Array of object ID sets or arrays
 * @returns Sorted combined ID array
 */
export function objectIdSet(idSets: Iterable<number>[]) {
    // Find object ID range
    let minId = Number.MAX_SAFE_INTEGER;
    let maxId = 0;
    let count = 0;
    idSets.forEach((ids) => {
        for (const id of ids) {
            if (id < minId) {
                minId = id;
            }
            if (id > maxId) {
                maxId = id;
            }
            count++;
        }
    });

    // Inclusive span: IDs from minId through maxId need (maxId - minId + 1) bits.
    const span = maxId - minId + 1;

    // Use a compact bitset (1 bit per possible ID in [minId, maxId]) stored in a Uint8Array
    // instead of a Set when IDs are dense enough. The threshold defines the minimum fraction
    // of IDs present in the range for which the bitset is expected to be more memory-efficient
    // (and typically faster to query) than a Set; profiling has shown ~5% density to be a
    // reasonable cutoff.
    const threshold = 0.05;

    if (count >= Math.max(1, span * threshold)) {
        // Use array
        const allIds = new Uint8Array(Math.ceil(span / 8));
        let count = 0;
        idSets.forEach((ids) => {
            for (const id of ids) {
                const offset = id - minId;
                const byteIndex = Math.floor(offset / 8);
                const bit = 1 << (offset % 8);
                if ((allIds[byteIndex] & bit) === 0) {
                    allIds[byteIndex] |= bit;
                    count++;
                }
            }
        });

        const result = createArrayObjectIdSet(allIds, minId, count);
        return result;
    } else {
        // Use set
        const allIds = new Set<number>();
        idSets.forEach((ids) => {
            for (const id of ids) {
                allIds.add(id);
            }
        });

        return createSetObjectIdSet(allIds);
    }
}

type ObjectIdSet = {
    has: (id: number) => boolean;
    delete: (id: number) => boolean;
    toArray: () => Uint32Array;
    clone: () => ObjectIdSet;
    size: () => number;
};

function createArrayObjectIdSet(allIds: Uint8Array, minId: number, count: number): ObjectIdSet {
    return {
        has: (id: number) => {
            const offset = id - minId;
            const byteIndex = Math.floor(offset / 8);
            const bit = 1 << (offset % 8);
            return byteIndex >= 0 && byteIndex < allIds.length && (allIds[byteIndex] & bit) !== 0;
        },
        delete: (id: number) => {
            const offset = id - minId;
            const byteIndex = Math.floor(offset / 8);
            const bit = 1 << (offset % 8);
            if (byteIndex >= 0 && byteIndex < allIds.length && (allIds[byteIndex] & bit) !== 0) {
                allIds[byteIndex] &= ~bit;
                count--;
                return true;
            }
            return false;
        },
        toArray: () => {
            const result = new Uint32Array(count);
            let offset = minId;
            let resultIndex = 0;
            for (let i = 0; i < allIds.length; i++) {
                if (allIds[i] !== 0) {
                    for (let j = 0; j < 8; j++) {
                        if ((allIds[i] & (1 << j)) !== 0) {
                            result[resultIndex++] = offset + j;
                        }
                    }
                }
                offset += 8;
            }
            return result;
        },
        clone: () => {
            return createArrayObjectIdSet(allIds.slice(), minId, count);
        },
        size: () => count,
    };
}

function createSetObjectIdSet(allIds: Set<number>): ObjectIdSet {
    return {
        has: (id: number) => allIds.has(id),
        delete: (id: number) => allIds.delete(id),
        toArray: () => new Uint32Array(allIds).sort(),
        clone: () => createSetObjectIdSet(new Set(allIds)),
        size: () => allIds.size,
    };
}

function areArraysEqual<T>(a: T[], b: T[]) {
    return a === b || (a.length === b.length && a.every((e, i) => e === b[i]));
}

function keepMapKeys<K, T>(cache: Map<K, T>, keys: K[]) {
    cache.forEach((_, k) => {
        if (!keys.includes(k)) {
            cache.delete(k);
        }
    });
}

function keepMapKeysOfAnotherMap<K, T>(cache: Map<K, T>, anotherMapOrSet: Map<K, unknown> | Set<K>) {
    cache.forEach((_, k) => {
        if (!anotherMapOrSet.has(k)) {
            cache.delete(k);
        }
    });
}

function rgbaColorFromStringKey(colorKey: string): VecRGBA {
    const components = colorKey.split("|");
    return [
        parseFloat(components[0]),
        parseFloat(components[1]),
        parseFloat(components[2]),
        parseFloat(components[3]),
    ] as VecRGBA;
}

/**
 * Compute the hidden IDs for the hidden highlight group.
 * In Procore mode, keep frozen IDs in the hidden highlight group so that the
 * hidden highlight (which comes last in Procore ordering) overrides selection
 * highlights, preventing frozen+selected objects from reappearing.
 * In standalone mode, frozen IDs are filtered out because they are handled
 * by the frozen highlight group instead.
 */
export function computeHiddenIds(
    isProcoreDesign: boolean,
    hidden: number[],
    hiddenTurnedFrozenIds: ReadonlySet<number>,
): number[] {
    return isProcoreDesign || hiddenTurnedFrozenIds.size === 0
        ? hidden
        : hidden.filter((id) => !hiddenTurnedFrozenIds.has(id));
}

export interface PostFrozenHighlightInputs {
    cacheHiddenGroups: RenderStateHighlightGroup | undefined;
    cacheHiddenPropertyTreeGroups: RenderStateHighlightGroup | undefined;
    hiddenHighlight: RenderStateHighlightGroup;
    basket: RenderStateHighlightGroup;
    normalColorOverrides: RenderStateHighlightGroup[];
    xRayColorOverrides: RenderStateHighlightGroup[];
    colored: RenderStateHighlightGroup[];
    coloredPropertyTree: RenderStateHighlightGroup[];
    coloredDeviation: RenderStateHighlightGroup | undefined;
    secondary: RenderStateHighlightGroup;
    formsNew: RenderStateHighlightGroup;
    formsOngoing: RenderStateHighlightGroup;
    formsCompleted: RenderStateHighlightGroup;
    formsFinished: RenderStateHighlightGroup;
    clash1: RenderStateHighlightGroup;
    clash2: RenderStateHighlightGroup;
    markerDragHover: RenderStateHighlightGroup | undefined;
    primaryNonTerrain: RenderStateHighlightGroup;
    primaryTerrain: RenderStateHighlightGroup;
    sectionPlaneHover: RenderStateHighlightGroup | null;
    selectedColorOverrides: RenderStateHighlightGroup[];
    forbidden: RenderStateHighlightGroup | undefined;
}

/**
 * Build the post-frozen highlight groups in the correct order.
 *
 * In Procore mode the hidden highlight is placed LAST so it overrides
 * selection highlights for frozen+selected objects.
 * In standalone mode the hidden highlight is placed early (right after
 * cache hidden groups) so subsequent selection highlights can still
 * make objects visible.
 */
export function buildPostFrozenHighlights(
    isProcoreDesign: boolean,
    g: PostFrozenHighlightInputs,
): RenderStateHighlightGroup[] {
    if (isProcoreDesign) {
        return [
            // Procore ordering
            g.cacheHiddenGroups,
            g.cacheHiddenPropertyTreeGroups,
            g.basket,
            ...g.normalColorOverrides,
            ...g.xRayColorOverrides,
            ...g.colored,
            ...g.coloredPropertyTree,
            g.coloredDeviation,
            g.secondary,
            g.formsNew,
            g.formsOngoing,
            g.formsCompleted,
            g.formsFinished,
            g.clash1,
            g.clash2,
            g.markerDragHover,
            g.primaryNonTerrain,
            g.primaryTerrain,
            g.sectionPlaneHover,
            ...g.selectedColorOverrides,
            g.forbidden,
            g.hiddenHighlight,
        ].filter(Boolean) as RenderStateHighlightGroup[];
    }

    return [
        // Standalone ordering
        g.cacheHiddenGroups,
        g.cacheHiddenPropertyTreeGroups,
        g.hiddenHighlight,
        g.basket,
        ...g.normalColorOverrides,
        ...g.xRayColorOverrides,
        ...g.colored,
        ...g.coloredPropertyTree,
        g.coloredDeviation,
        g.secondary,
        g.formsNew,
        g.formsOngoing,
        g.formsCompleted,
        g.formsFinished,
        g.clash1,
        g.clash2,
        g.markerDragHover,
        g.primaryNonTerrain,
        g.primaryTerrain,
        g.sectionPlaneHover,
        ...g.selectedColorOverrides,
        g.forbidden,
    ].filter(Boolean) as RenderStateHighlightGroup[];
}

type HighlightGroupWithNumberArray = Omit<RenderStateHighlightGroup, "objectIds"> & {
    objectIds: Uint32Array;
};

/**
 * Frozen highlight is special - it forces engine to remove objects from the scene, not just hide them.
 * This means when we want to show them again - it's not enough to just change highlight action -
 * nodes with those objects need to be reloaded.
 * For this purpose engine is diffing "filter" groups, but in a limited way
 * - If default visibility changed or more than one "filter" highlight - reload the scene.
 *   We want to avoid it because it causes scene rebuild and takes time.
 * - If single "filter" highlight added/removed/changed - it finds a diff and decides which nodes to reload exactly.
 *
 * The problem with diffing is that any highlight coming after "filter" highlight may override it,
 * which means we need to take all those subsequent highlights into account and run diffing on more data and more often
 * (e.g. when user just selects a single object we need to check that it doesn't override "filter" highlight).
 *
 * Engine is more generic and solving this problem there appears to be more complicated/expensive to me.
 * Here we control highlights and can optimize for this limitation
 * - Always single "filter" highlight
 * - Caching
 */
export class SingleFrozenHighlight {
    private frozenGroups: Iterable<ObjectId>[] = [];
    private relevantSubsequents: Iterable<ObjectId>[] = [];
    private highlightGroup: HighlightGroupWithNumberArray | undefined;
    private rawIds: ObjectIdSet | undefined;
    private skipSet = new WeakSet<Iterable<ObjectId>>();

    update(frozenGroups: Iterable<ObjectId>[], subsequentHighlights: RenderStateHighlightGroup[]) {
        // If any frozen group changed - collect all frozen group object IDs first
        let rawIds = this.rawIds;
        if (!rawIds || !areArraysEqual(this.frozenGroups, frozenGroups)) {
            rawIds = objectIdSet(frozenGroups);
            this.skipSet = new WeakSet();
            this.relevantSubsequents = [];
        }

        // Ignore hidden subsequents, because they are effectively the same
        // We also assume there are no other "filter" highlights
        let relevantSubsequents = subsequentHighlights
            .filter((other) => other.action !== "hide" && !this.skipSet.has(other.objectIds))
            .map((other) => other.objectIds);

        // Exclude subsequent highlight object IDs from frozen group object IDs
        // and create highlight group.
        let highlightGroup = this.highlightGroup;
        if (
            !highlightGroup ||
            rawIds !== this.rawIds ||
            !areArraysEqual(this.relevantSubsequents, relevantSubsequents)
        ) {
            const effectiveIds = this.applySubsequent(rawIds, relevantSubsequents);

            // Remove subsequents newly added to the skip set
            relevantSubsequents = relevantSubsequents.filter((other) => !this.skipSet.has(other));

            highlightGroup = {
                objectIds: effectiveIds.toArray(),
                action: "filter",
            };
        }

        this.frozenGroups = frozenGroups;
        this.highlightGroup = highlightGroup;
        this.rawIds = rawIds;
        this.relevantSubsequents = relevantSubsequents;

        return highlightGroup;
    }

    /**
     * Exclude others object IDs from raw IDs.
     * Copy on write, so if no matches - no new object ID set is created.
     */
    private applySubsequent(rawIds: ObjectIdSet, others: Iterable<ObjectId>[]) {
        let nextRawIds = rawIds;

        for (const other of others) {
            if (nextRawIds.size() === 0) {
                break;
            }

            let hasMatches = false;
            for (const id of other) {
                // Test that ID is in rawIds (instead of nextRawIds) to maintain correct skipSet.
                // Otherwise other can get into skipSet by mistake in this scenario:
                // other1 is [1], other2 is [1], other2 gets into skipSet because it's the same as other1.
                // Later if other1 is removed - other2 will still be in skipSet, but it shouldn't.
                if (!hasMatches && rawIds.has(id)) {
                    hasMatches = true;
                    if (nextRawIds === rawIds) {
                        nextRawIds = rawIds.clone();
                    }
                }
                nextRawIds.delete(id);
            }

            if (!hasMatches) {
                this.skipSet.add(other);
            }
        }

        return nextRawIds;
    }
}
